import * as React from "react";
import styles from "./ContactCard.module.css";

export interface PersonData {
  bizCardFirstName: string | null;
  bizCardVCardFirstName: string | null;
  bizCardLastName: string | null;
  bizCardSuffix: string | null;
  bizCardCompany: "ACD" | "SCC" | null;
  bizCardTitle: string | null;
  bizCardCellPhone: string | null;
  bizCardDirectPhone: string | null;
  bizCardOfficePhone: string | null;
  bizCardOfficeExt: string | null;
  bizCardEmail: string | null;
  bizCardQRCodeId: string | null;
  bizCardPhoto: string | null;
}

const COMPANY_DISPLAY: Record<string, string> = {
  ACD: "American Community Developers",
  SCC: "St. Clair Construction",
};

function formatPhone(digits: string): string {
  // Expects a 10-digit string like "8005551212" and formats it as "(800) 555-1212"
  const d = digits.replace(/\D/g, "");
  if (d.length === 10) {
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }
  return digits;
}

function isPresent(value: string | null | undefined): value is string {
  return value !== null && value !== undefined && value !== "";
}

interface ContactCardProps {
  person: PersonData;
}

const ContactCard: React.FC<ContactCardProps> = ({ person }) => {
  const firstName = isPresent(person.bizCardVCardFirstName)
    ? person.bizCardVCardFirstName
    : isPresent(person.bizCardFirstName) ? person.bizCardFirstName : "";
  const lastName = isPresent(person.bizCardLastName) ? person.bizCardLastName : "";
  const suffix = isPresent(person.bizCardSuffix) ? person.bizCardSuffix : null;

  const fullName = [firstName, lastName].filter(Boolean).join(" ") + (suffix ? `, ${suffix}` : "");

  const companyDisplay =
    isPresent(person.bizCardCompany) && COMPANY_DISPLAY[person.bizCardCompany]
      ? COMPANY_DISPLAY[person.bizCardCompany]
      : null;

  return (
    <div className={styles["card"]}>
      {isPresent(person.bizCardPhoto) && (
        <img
          className={styles["photo"]}
          src={person.bizCardPhoto}
          alt={fullName || "Contact photo"}
        />
      )}
      <div className={styles["info"]}>
        {fullName && <h1 className={styles["name"]}>{fullName}</h1>}
        {isPresent(person.bizCardTitle) && <p className={styles["title"]}>{person.bizCardTitle}</p>}
        {companyDisplay && <p className={styles["company"]}>{companyDisplay}</p>}

        <ul className={styles["contactList"]}>
          {isPresent(person.bizCardCellPhone) && (
            <li className={styles["contactItem"]}>
              <span className={styles["contactIcon"]}>📱</span>
              <span className={styles["contactLabel"]}>Mobile</span>
              <a
                className={styles["contactLink"]}
                href={`tel:${person.bizCardCellPhone}`}
              >
                {formatPhone(person.bizCardCellPhone)}
              </a>
            </li>
          )}
          {isPresent(person.bizCardDirectPhone) && (
            <li className={styles["contactItem"]}>
              <span className={styles["contactIcon"]}>☎</span>
              <span className={styles["contactLabel"]}>Direct</span>
              <a
                className={styles["contactLink"]}
                href={`tel:${person.bizCardDirectPhone}`}
              >
                {formatPhone(person.bizCardDirectPhone)}
              </a>
            </li>
          )}
          {isPresent(person.bizCardOfficePhone) && (
            <li className={styles["contactItem"]}>
              <span className={styles["contactIcon"]}>🏢</span>
              <span className={styles["contactLabel"]}>Office</span>
              <a
                className={styles["contactLink"]}
                href={`tel:${person.bizCardOfficePhone}`}
              >
                {formatPhone(person.bizCardOfficePhone)}
                {isPresent(person.bizCardOfficeExt) ? ` x${person.bizCardOfficeExt}` : ""}
              </a>
            </li>
          )}
          {isPresent(person.bizCardEmail) && (
            <li className={styles["contactItem"]}>
              <span className={styles["contactIcon"]}>✉</span>
              <span className={styles["contactLabel"]}></span>
              <a
                className={styles["contactLink"]}
                href={`mailto:${person.bizCardEmail}`}
              >
                {person.bizCardEmail}
              </a>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default ContactCard;
